// src/components/UI/useModalA11y.ts
// Reusable modal a11y behavior: on open it (optionally) moves focus into the
// dialog, traps Tab within it, closes on Escape, and restores focus to the
// trigger on close. Runs once on mount and reads the latest onClose via a ref,
// so re-renders never re-steal focus. Attach the returned ref to your container
// and add the ARIA attributes yourself — handy for large existing modals where
// wrapping the whole subtree isn't practical. `<UI/Modal>` wraps this for the
// common case.

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalA11yOptions {
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Keep Tab focus within the dialog. Default true. Set false for non-blocking panels. */
  trapFocus?: boolean;
  /** Move focus into the dialog on open. Default true. */
  autoFocus?: boolean;
  /** Restore focus to the trigger on close. Default true. */
  restoreFocus?: boolean;
  /** Element to focus on open; defaults to the first focusable in the dialog. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

export function useModalA11y(
  onClose: () => void,
  options: ModalA11yOptions = {}
): RefObject<HTMLDivElement | null> {
  const {
    closeOnEscape = true,
    trapFocus = true,
    autoFocus = true,
    restoreFocus = true,
    initialFocusRef,
  } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = containerRef.current;

    if (autoFocus) {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else {
        const first = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (first ?? container)?.focus();
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (!trapFocus || e.key !== 'Tab' || !container) return;

      const nodes = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && (active === first || !container.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (restoreFocus) previouslyFocused?.focus?.();
    };
    // Mount-only: options are fixed per usage and onClose is read via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return containerRef;
}
